import type { Annotation, Item } from '@mystiq/contracts';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { keys, useWorkspaceMutation } from '../api/hooks.ts';
import { sameOrigin } from '../api/client.ts';
import { useApi } from '../auth/session.tsx';
import { ICON, OpenOutside, Write } from '../icons.ts';
import { timeAgo } from '../lib/format.ts';
import { BridgeGate, newNonce } from '../lib/site-bridge.ts';
import { CommentsProvider, CommentsSection, useComments } from './Comments.tsx';
import { byline, Details, FolderCrumb, ItemActions, Processing, RelatedSection, TitleField } from './ItemParts.tsx';
import { ErrorLine } from './ui.tsx';

/**
 * A Site: an interactive page of its own, shown in a sandboxed frame (a page
 * of the app's own address under /site, sealed off from the app by the
 * sandbox). Every version stays as it was; a new one is written from the
 * current page on request. Selecting text in the page proposes what a comment
 * points at; the comment itself is written and saved here.
 */
export function SiteView({ ws, item }: { ws: string; item: Item }) {
  return (
    <CommentsProvider>
      <SiteBody ws={ws} item={item} />
    </CommentsProvider>
  );
}

function SiteBody({ ws, item }: { ws: string; item: Item }) {
  const api = useApi();
  const propose = useComments()?.propose;
  const current = item.currentRevisionId ?? '';
  const [chosen, setChosen] = useState<string | null>(null);
  const shown = chosen ?? current;
  const versions = useQuery({ queryKey: keys.revisions(ws, item.id), queryFn: () => api.revisions(ws, item.id) });
  const preview = useQuery({
    queryKey: keys.sitePreview(ws, item.id, shown),
    queryFn: () => api.readSite(ws, item.id, shown),
    enabled: Boolean(shown),
    // Fetched once per version shown: the link only opens the frame, and the page in it keeps running after the link expires.
    // Fetching it again would reload the Site and lose what the person was doing in it.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const version = preview.data;
  const canComment = item.permittedActions.includes('comment');
  const numberOf = (revisionId: string) => versions.data?.find((entry) => entry.revisionId === revisionId)?.number ?? null;
  const versionName = (revisionId: string) => {
    const number = numberOf(revisionId) ?? (version?.revisionId === revisionId ? version.number : null);
    return number ? `version ${number}` : null;
  };

  // The frame: a fresh nonce for every page load; only messages carrying it, from this frame, for this version count.
  const frame = useRef<HTMLIFrameElement>(null);
  const nonce = useMemo(() => (version ? newNonce() : ''), [version]);
  const waiting = useRef<string | null>(null);
  const highlight = (path: string) => frame.current?.contentWindow?.postMessage({ type: 'mystiq-site:highlight', path }, '*');
  useEffect(() => {
    if (!version) return;
    const gate = new BridgeGate({ frame: () => frame.current?.contentWindow, nonce, revisionId: version.revisionId });
    const onMessage = (event: MessageEvent) => {
      const message = gate.read(event);
      if (!message) return;
      if (message.type === 'ready') {
        if (waiting.current) highlight(waiting.current);
        waiting.current = null;
      } else if (message.path && canComment && propose) {
        propose({ anchor: { kind: 'element', path: message.path, quote: message.quote }, revisionId: version.revisionId, label: message.quote });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [version, nonce, canComment, propose]);

  // Revising: a new version written by AI from the current page and the sources it was made from.
  const [revising, setRevising] = useState(false);
  const [instructions, setInstructions] = useState('');
  const lastRevise = useRef<{ text: string; key: string } | null>(null);
  const revise = useWorkspaceMutation(ws, async (text: string) => {
    // A repeat after a lost answer is the same request; once it went through, asking again is a new one.
    if (lastRevise.current?.text !== text) lastRevise.current = { text, key: crypto.randomUUID() };
    const operation = await api.reviseSite(ws, item.id, text, lastRevise.current.key);
    lastRevise.current = null;
    return operation;
  });
  const stage = item.stages.find((entry) => entry.kind === 'generate_site');
  const writing = stage?.status === 'queued' || stage?.status === 'running';
  const newest = versions.data?.[0];
  const failedAfterNewest = stage && (stage.status === 'failed' || stage.status === 'cancelled') && (!newest || stage.updatedAt > newest.createdAt);
  // A revision stopped because the Site changed meanwhile is asked for again from the current version, not retried.
  const outdated = stage?.errorCode === 'CONFLICT';
  const retry = useWorkspaceMutation(ws, (operationId: string) => api.retryOperation(ws, operationId));

  const show = {
    can: (annotation: Annotation) => annotation.anchor.kind === 'element' || annotation.revisionId !== shown,
    run: (annotation: Annotation) => {
      const path = annotation.anchor.kind === 'element' ? annotation.anchor.path : null;
      if (annotation.revisionId !== shown) {
        waiting.current = path;
        setChosen(annotation.revisionId === current ? null : annotation.revisionId);
      } else if (path) {
        highlight(path);
      }
      frame.current?.scrollIntoView({ block: 'nearest' });
    },
  };

  return (
    <div className="page">
      <FolderCrumb ws={ws} item={item} />
      <TitleField key={`title-${item.id}`} ws={ws} item={item} placeholder="Untitled Site" />
      <p className="meta">
        Site{newest ? ` · ${newest.number === 1 ? '1 version' : `${newest.number} versions`}` : ''} · {byline(item)}
      </p>

      <div className="toolbar">
        {item.permittedActions.includes('revise') && (
          <button className="button button-primary" disabled={writing} onClick={() => setRevising(true)}>
            <Write size={ICON.nav} stroke={ICON.stroke} />
            Revise
          </button>
        )}
        {versions.data && versions.data.length > 1 && (
          <select
            className="select select-inline"
            aria-label="Version"
            value={shown}
            onChange={(event) => setChosen(event.target.value === current ? null : event.target.value)}
          >
            {versions.data.map((entry) => (
              <option key={entry.revisionId} value={entry.revisionId}>
                {`Version ${entry.number}${entry.current ? ' (current)' : ''} · ${timeAgo(entry.createdAt)}`}
              </option>
            ))}
            {/* An older version than the list holds (opened from a comment) still has its entry. */}
            {version && !versions.data.some((entry) => entry.revisionId === version.revisionId) && <option value={version.revisionId}>{`Version ${version.number}`}</option>}
          </select>
        )}
        {version && (
          <a className="button" href={sameOrigin(version.previewUrl)} target="_blank" rel="noopener noreferrer">
            <OpenOutside size={ICON.nav} stroke={ICON.stroke} />
            Open in a new tab
          </a>
        )}
      </div>

      {revising && (
        <form
          className="revise-panel"
          onSubmit={async (event) => {
            event.preventDefault();
            const text = instructions.trim();
            if (!text) return;
            if (await revise.mutateAsync(text).then(() => true, () => false)) {
              setRevising(false);
              setInstructions('');
              setChosen(null);
            }
          }}
        >
          <label className="field">
            <span className="field-label">What should change?</span>
            <textarea
              className="textarea textarea-short"
              maxLength={4000}
              value={instructions}
              placeholder="For example: add a score at the end, and make the buttons larger"
              onChange={(event) => setInstructions(event.target.value)}
            />
          </label>
          <p className="meta">AI writes a new version from the current one. Earlier versions, their comments and any share link stay as they are.</p>
          <ErrorLine error={revise.error} />
          <div className="row-actions">
            <button className="button button-primary" type="submit" disabled={revise.isPending || !instructions.trim()}>
              Write new version
            </button>
            <button className="button" type="button" onClick={() => setRevising(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {writing && (
        <p className="meta" role="status">
          <span className="state">Writing a new version.</span> It appears here when it is ready.
        </p>
      )}
      {failedAfterNewest && stage && (
        <div className="stack">
          <p role="status">
            <span className="state">The new version was not written.</span> {stage.errorMessage ?? ''}
          </p>
          {item.permittedActions.includes('revise') && !outdated && (
            <div>
              <button className="button button-inline" disabled={retry.isPending} onClick={() => retry.mutate(stage.operationId)}>
                Retry
              </button>
            </div>
          )}
          <ErrorLine error={retry.error} />
        </div>
      )}

      {chosen && chosen !== current && (
        <p className="meta" role="status">
          <span className="state">An earlier version.</span> {numberOf(current) ? `Version ${numberOf(current)} is the current one. ` : ''}
          <button className="link-button" onClick={() => setChosen(null)}>
            Show the current version
          </button>
        </p>
      )}
      <div className="site-stage">
        {version ? (
          <iframe
            key={nonce}
            ref={frame}
            className="site-frame"
            title={`Site: ${item.displayTitle}`}
            sandbox="allow-scripts"
            allow=""
            referrerPolicy="no-referrer"
            src={`${sameOrigin(version.previewUrl)}#mystiq=${nonce}.${version.revisionId}`}
          />
        ) : (
          <p className="meta site-loading">{preview.isError ? 'Could not open this Site.' : 'Opening…'}</p>
        )}
      </div>
      <ErrorLine error={preview.error} />
      <p className="meta">
        {version?.made === 'imported' ? 'Opened from an HTML file. ' : version?.instructions ? `Asked for: “${version.instructions}”. ` : ''}
        The Site runs separately from Mystiq: it cannot reach your workspace, your account or the internet.
      </p>

      <CommentsSection
        ws={ws}
        item={item}
        hint="Select text in the Site to comment on that part, or comment on the whole Site."
        versionName={versionName}
        show={show}
      />
      <Details key={`details-${item.id}`} ws={ws} item={item} />
      <Processing ws={ws} item={item} />
      <RelatedSection ws={ws} itemId={item.id} />
      <ItemActions ws={ws} item={item} shareRevision={version ? { revisionId: version.revisionId, label: versionName(version.revisionId) ?? 'this version' } : undefined} />
    </div>
  );
}
